var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useEffect, useMemo, useRef, useCallback } from "react";
import get from "lodash/get";
import set from "lodash/set";
import noop from "lodash/noop";
import omit from "lodash/omit";
import merge from "lodash/merge";
import pickBy from "lodash/pickBy";
import { connect } from '../../store/connect';
import { getBookData, getBookEntities, getInitialData, setRequestQueryString } from "../../store/actions/response.actions";
import { CHECKOUT_CONTEXT_ENUM, setCheckoutContext, updateCart, updateForm } from "../../store/actions";
import { getResponseManager } from "../../responseManager/BeeResponseManager";
import { NoDataToDisplay } from "../NoDataToDisplay/NoDataToDisplay";
import { getBookManager } from "../../models/BookModel";
import { ReactiveForm } from '../Form';
import { componentMapper, validatorMapper, componentTypes } from "../Form/form-constants";
import { normalizeCart, getNormalizedCartItems, getCartItemIds, useCartListener } from "./cart.helpers";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import parse from "html-react-parser";
import { Loader } from "../Loader/Loader";
import { API_METHODS_ENUM } from "../../httpService";
import { LoaderState } from "../../store/actions/loader.actions";
import { useConfig, usePresentationComponent } from "../../AppContext";
import { isAdminIntegration } from "../../store/statePresenter";
import { hasAllowAllEvents, hasWaitlistBookings } from "../BookingForm/booking-form.helpers";
import qs from "querystring";
import { getBookingFormManager } from "../../FormManager/BookingFormManager";
var cartFactory = function () {
    var CartRenderer = function (_a) {
        var title = _a.title, description = _a.description, children = _a.children;
        return (React.createElement(React.Fragment, null,
            title,
            description,
            children));
    };
    var Cart = function (props) {
        var CartComponent = usePresentationComponent(["cart.cart"]) || CartRenderer;
        var classNameGetter = useConfig("classNames.cart", function () { return ""; })[0];
        var t = useTranslation().t;
        var goBack = useHistory().goBack;
        var initialValues = useMemo(function () { return props.mergedForm; }, [JSON.stringify(props.mergedForm)]);
        var cartItems = get(initialValues, "booking_data.cart_items", []);
        var initialQueryRef = useRef("");
        var ftimespans = props.timespanIds.join(",");
        var onCheckout = props.onCheckout;
        var takeMeBack = function () {
            goBack();
        };
        var onSubmit = function (callback) { return function (e, state) {
            if (!(state === null || state === void 0 ? void 0 : state.valid))
                return;
            props.setCheckoutContext(CHECKOUT_CONTEXT_ENUM.CART);
            callback(state.values);
        }; };
        var goBackCallback = useCallback(takeMeBack, []);
        var addToCartCallback = useCallback(onSubmit(onCheckout), [onCheckout]);
        var schema = useMemo(function () { return ({
            fields: [
                props.isAdminIntegration ? null : {
                    component: componentTypes.FORM_BUTTONS,
                    context: "cart",
                    name: "form-buttons-top",
                    buttons: [
                        props.isAdminIntegration ? null : { label: t("cart.back"), variant: "clear", onClick: goBackCallback },
                        { label: t("cart.checkout"), onClick: addToCartCallback, disabled: props.disableCheckout },
                    ].filter(Boolean)
                },
                props.cartSchema,
                {
                    component: componentTypes.FORM_BUTTONS,
                    context: "cart",
                    name: "form-buttons-bottom",
                    buttons: [
                        props.isAdminIntegration ? null : { label: t("cart.back"), variant: "clear", onClick: goBackCallback },
                        {
                            label: props.isAdminIntegration ? t("admin_booking_form.checkout") : t("cart.checkout"),
                            onClick: addToCartCallback,
                            disabled: props.disableCheckout
                        },
                    ].filter(Boolean)
                }
            ].filter(Boolean),
        }); }, [JSON.stringify(props.cartSchema)]);
        // Add Cart event listener to update cart with new items
        useCartListener(function (cart) {
            if (!initialValues)
                return;
            props.updateForm({
                name: props.name,
                form: set(initialValues, "booking_data.cart_items", cart)
            }, "cart cartlistener");
        }, { noEmit: true });
        var queryString = qs.stringify(pickBy({
            ftimespans: ftimespans,
            allow_all_events: (!!props.isAdminIntegration && hasAllowAllEvents(initialValues)),
            waitlist_bookings: (!!props.isAdminIntegration && hasWaitlistBookings(initialValues))
        }, Boolean));
        var callBookApi = (queryString !== initialQueryRef.current);
        useEffect(function () { return function () {
            props.setRequestQueryString(API_METHODS_ENUM.GET_BOOK_ENTITIES, null);
        }; }, []);
        // Load All Events.
        // Needed to list cart items.
        useEffect(function () {
            if (!callBookApi || !ftimespans)
                return;
            initialQueryRef.current = queryString;
            props.getBookEntities(queryString);
            props.setRequestQueryString(API_METHODS_ENUM.GET_BOOK_ENTITIES, queryString);
        }, [callBookApi, queryString, ftimespans]);
        // Update cart to persistentState
        var cartWithoutParticipantInfo = (cartItems === null || cartItems === void 0 ? void 0 : cartItems.map(function (item) { return omit(item, "booking.personal_data_index"); })) || [];
        useEffect(function () {
            if (props.isLoading)
                return;
            props.updateCart(cartWithoutParticipantInfo);
        }, [JSON.stringify(cartWithoutParticipantInfo)]);
        var getCart = function () {
            if (props.hasErrored)
                return null;
            if (ftimespans && (props.isLoading || (props.hasBookCart && !props.cartSchema)))
                return React.createElement(Loader, null);
            if (!ftimespans || !props.hasBookCart || !props.cartItemsCount)
                return React.createElement(NoDataToDisplay, { message: t("cart.no_data_to_display") });
            return (React.createElement(ReactiveForm, { name: props.name, schema: schema, componentMapper: componentMapper, validatorMapper: validatorMapper, initialValues: initialValues, onSubmit: noop }));
        };
        return (React.createElement(CartComponent, { className: classNameGetter({ itemCount: cartItems.length }), title: parse(t("cart.title")), description: parse(t("cart.description")), itemCount: props.cartItemsCount },
            " ",
            getCart(),
            " "));
    };
    var mapStateToProps = function (state) {
        var name = "cart";
        var adminIntegration = isAdminIntegration(state);
        return {
            name: name,
            isAdminIntegration: adminIntegration,
            form: get(state, "formReducer.forms." + name, null),
            adminSettings: get(state, "formReducer.forms.adminsettings"),
            cartItems: get(state, "cartReducer.cart_items", []),
            validationErrors: get(state, "appReducer.validationErrors", null),
            initialData: get(getInitialData(state.responseReducer), "response"),
            entities: get(getBookData(state.responseReducer), "response"),
            requestQueryString: get(getBookData(state.responseReducer), "requestQueryString", null),
            completedQueryString: get(getBookData(state.responseReducer), "queryString", null),
            loaderState: state.loaderReducer,
        };
    };
    var mergeProps = function (state, dispatch, props) {
        var _a, _b;
        var initialDataRm = state.initialData && getResponseManager(state.initialData);
        var options = initialDataRm === null || initialDataRm === void 0 ? void 0 : initialDataRm.options();
        var optionEntities = initialDataRm === null || initialDataRm === void 0 ? void 0 : initialDataRm.entities();
        var entities = state.entities && getResponseManager(state.entities).entities();
        var hasBookCart = state.isAdminIntegration || get(options, "enable_cart", false);
        var loaderState = new LoaderState(state.loaderState, { keys: [API_METHODS_ENUM.GET_BOOK_ENTITIES] });
        var cartItems = getNormalizedCartItems(state.cartItems);
        var timespanIds = getCartItemIds(cartItems);
        var adminSettings = state.adminSettings || {};
        var cartForm = normalizeCart(__assign(__assign({}, state.form), { cart: ((_b = (_a = state.form) === null || _a === void 0 ? void 0 : _a.booking_data) === null || _b === void 0 ? void 0 : _b.cart_items) || cartItems }));
        var mergedProps = __assign(__assign(__assign({}, dispatch), props), { hasBookCart: hasBookCart,
            timespanIds: timespanIds, name: state.name, hideCheckout: false, isAdminIntegration: state.isAdminIntegration, mergedForm: merge(cartForm || { booking_data: { cart_items: cartItems } }, adminSettings), otherStages: [], topFormFieldPaths: [], cartSchema: null, cartItemsCount: (cartItems === null || cartItems === void 0 ? void 0 : cartItems.length) || 0, hasErrored: loaderState.hasErrored(), isLoading: !entities || loaderState.isLoading() || loaderState.hasErrored() });
        if (entities && hasBookCart) {
            var bm = getBookManager(__assign(__assign({}, entities), optionEntities), __assign(__assign({}, options), { skip_form_data: true, displayTimespanTitleInParticipants: true, showParticipantDropdown: true, nonBindingWaitlistIsBookedOut: true, isAdminIntegration: state.isAdminIntegration, disableAvailabilityCheck: state.isAdminIntegration && hasAllowAllEvents(state.adminSettings) }));
            var loadingPending = !state.requestQueryString || state.requestQueryString !== state.completedQueryString;
            if (loadingPending) {
                mergedProps.callBookApi = true;
                return mergedProps;
            }
            try {
                var mergedForm = bm.deriveCartForm(__assign({}, mergedProps.mergedForm), (cartForm === null || cartForm === void 0 ? void 0 : cartForm.booking_data.cart_items) || cartItems);
                var formManager = getBookingFormManager({ modelManager: bm, form: mergedForm }, {
                    nonBindingWaitlistIsBookedOut: true,
                    adminSettings: adminSettings
                });
                mergedProps.mergedForm = formManager.toJSON();
                mergedProps.cartSchema = bm.getCartForm(formManager.state);
                mergedProps.disableCheckout = !mergedProps.cartSchema.fields.some(function (_a) {
                    var quantity = _a.quantity;
                    return +quantity;
                });
            }
            catch (e) {
                mergedProps.callBookApi = loadingPending;
                mergedProps.isLoading = loadingPending;
            }
        }
        return mergedProps;
    };
    return connect(mapStateToProps, { getBookEntities: getBookEntities, setRequestQueryString: setRequestQueryString, setCheckoutContext: setCheckoutContext, updateCart: updateCart, updateForm: updateForm }, mergeProps)(Cart);
};
export var Cart = cartFactory();
